import React, { useEffect, useRef, useState } from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { googleAuthService } from '../services/googleAuth.js';

const GoogleLoginButton = ({ disableOneTap = false }) => {
  const buttonRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const { login, isTokenExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const handleSuccess = async (event) => {
      if (!mounted || isProcessing) return;
      
      setIsProcessing(true);
      try {
        const result = await login(event.detail.token);
        if (result.success) {
          // Stay on current page if on summary page, otherwise go home
          if (location.pathname === '/summary') {
            // Don't navigate, just stay on the summary page
            return;
          }
          navigate('/');
        } else {
          // Handle login failure - dispatch custom event for error handling
          const errorEvent = new CustomEvent('loginError', {
            detail: {
              message: result.error || 'Login failed',
              isInactive: result.isInactive
            }
          });
          window.dispatchEvent(errorEvent);
        }
      } finally {
        if (mounted) setIsProcessing(false);
      }
    };

    const handleError = () => {
      if (mounted) setShowFallback(true);
    };

    window.addEventListener('googleLoginSuccess', handleSuccess);
    window.addEventListener('googleLoginError', handleError);

    // Render button
    if (buttonRef.current) {
      googleAuthService.renderButton(buttonRef.current, { 
        showOneTap: !disableOneTap && !isTokenExpired 
      }).catch(() => {
        if (mounted) setShowFallback(true);
      });
    }

    return () => {
      mounted = false;
      window.removeEventListener('googleLoginSuccess', handleSuccess);
      window.removeEventListener('googleLoginError', handleError);
    };
  }, [login, isProcessing, navigate, location.pathname, disableOneTap, isTokenExpired]);

  if (showFallback) {
    return (
      <Button
        variant="outlined"
        size="large"
        startIcon={isProcessing ? <CircularProgress size={20} /> : <GoogleIcon />}
        onClick={() => alert('Google Sign-In not available')}
        disabled={isProcessing}
        sx={{
          py: 1.5,
          px: 3,
          textTransform: 'none',
          borderColor: '#dadce0',
          color: '#3c4043',
          minWidth: 200,
        }}
      >
        {isProcessing ? 'Signing in...' : 'Sign in with Google'}
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      {isProcessing ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'white',
            padding: '12px 24px',
            borderRadius: 1,
            border: '1px solid #dadce0',
            minHeight: '40px',
          }}
        >
          <CircularProgress size={20} />
          <span style={{ fontSize: '14px', color: '#3c4043' }}>Signing in...</span>
        </Box>
      ) : (
        <div ref={buttonRef} />
      )}
    </Box>
  );
};

export default GoogleLoginButton;